<script setup lang="ts">
// 운영 콘솔(최고관리자 전용). 운영자 내부용이라 한국어 고정. 모든 행동은 사유 필수 → 감사 기록.
// 일기·답장·사람 이름은 서버에 없어 여기서도 볼 수 없다(accounts.md §5).
import { onMounted, ref } from 'vue'
import { useAdminStore } from '../../stores/admin'

const admin = useAdminStore()
const emit = defineEmits<{ close: []; toast: [msg: string] }>()
type Tab = 'dash' | 'users' | 'audit'
const tab = ref<Tab>('dash')
const q = ref('')
const statusFilter = ref('')
const busy = ref(false)
const ACTIONS: Record<string, string> = { suspend: '정지', unsuspend: '정지 해제', logout_all: '세션 모두 종료', delete: '탈퇴 처리', cancel_delete: '탈퇴 취소' }

async function go(t: Tab) {
  tab.value = t; busy.value = true
  try {
    if (t === 'dash') await admin.loadMetrics()
    else if (t === 'users') await admin.loadUsers(q.value, statusFilter.value)
    else await admin.loadAudit()
  } catch (e: any) { emit('toast', e.message) } finally { busy.value = false }
}
async function doAction(u: any, action: string) {
  const reason = window.prompt(`${ACTIONS[action]} 사유를 입력하세요(감사 기록에 남습니다)`)
  if (!reason || !reason.trim()) return
  busy.value = true
  try { await admin.act(u.id, action, reason.trim()); await admin.loadUsers(q.value, statusFilter.value); emit('toast', `${ACTIONS[action]} 완료`) }
  catch (e: any) { emit('toast', e.message) } finally { busy.value = false }
}
const shortId = (id: string) => id.slice(0, 8)
const fmt = (iso?: string) => (iso ? iso.slice(0, 10) : '')
onMounted(() => go('dash'))
</script>

<template>
  <div class="adm-scrim" @click="emit('close')"></div>
  <div class="adm" role="dialog" aria-modal="true">
    <div class="adm-top"><b>운영 콘솔</b><button class="adm-x" @click="emit('close')">×</button></div>
    <div class="adm-tabs">
      <button :class="{ sel: tab === 'dash' }" @click="go('dash')">대시보드</button>
      <button :class="{ sel: tab === 'users' }" @click="go('users')">회원</button>
      <button :class="{ sel: tab === 'audit' }" @click="go('audit')">감사 기록</button>
    </div>

    <div v-if="tab === 'dash'" class="adm-dash">
      <template v-if="admin.metrics">
        <div class="adm-card"><b>{{ admin.metrics.users_total }}</b><span>전체 회원</span></div>
        <div class="adm-card"><b>{{ admin.metrics.by_status?.active || 0 }}</b><span>활성</span></div>
        <div class="adm-card"><b>{{ admin.metrics.by_status?.suspended || 0 }}</b><span>정지</span></div>
        <div class="adm-card"><b>{{ admin.metrics.by_status?.pending_deletion || 0 }}</b><span>탈퇴 대기</span></div>
        <div class="adm-card"><b>{{ admin.metrics.new_today }}</b><span>오늘 가입</span></div>
        <div class="adm-card"><b>{{ admin.metrics.users_synced }}</b><span>동기화 사용</span></div>
      </template>
    </div>

    <div v-else-if="tab === 'users'" class="adm-users">
      <div class="adm-search">
        <input v-model="q" placeholder="ID·가린 이메일 검색" @keydown.enter="go('users')" />
        <select v-model="statusFilter" @change="go('users')">
          <option value="">전체</option><option value="active">활성</option><option value="suspended">정지</option><option value="pending_deletion">탈퇴 대기</option>
        </select>
        <button @click="go('users')">조회</button>
      </div>
      <div class="adm-total">{{ admin.total }}명</div>
      <div v-for="u in admin.users" :key="u.id" class="adm-urow">
        <div class="adm-uinfo">
          <b>{{ shortId(u.id) }}</b> <span class="adm-badge" :data-s="u.status">{{ u.status }}</span>
          <small>{{ u.email_hint }} · {{ u.auth_provider }} · {{ u.country }} · 가입 {{ fmt(u.created_at) }} · 기기 {{ u.devices }}</small>
        </div>
        <div class="adm-uact">
          <button v-if="u.status !== 'suspended'" @click="doAction(u, 'suspend')">정지</button>
          <button v-else @click="doAction(u, 'unsuspend')">해제</button>
          <button @click="doAction(u, 'logout_all')">세션종료</button>
          <button v-if="u.status !== 'pending_deletion'" class="danger" @click="doAction(u, 'delete')">탈퇴</button>
          <button v-else @click="doAction(u, 'cancel_delete')">탈퇴취소</button>
        </div>
      </div>
      <p v-if="!admin.users.length && !busy" class="adm-empty">회원이 없어요</p>
    </div>

    <div v-else class="adm-audit">
      <div v-for="(a, i) in admin.audit" :key="i" class="adm-arow">
        <div><b>{{ ACTIONS[a.action] || a.action }}</b> <small>{{ a.at.slice(0, 16).replace('T', ' ') }}</small></div>
        <small>관리자 {{ shortId(a.admin_id) }} → {{ a.target_user ? shortId(a.target_user) : '-' }} · {{ a.reason }}</small>
      </div>
      <p v-if="!admin.audit.length && !busy" class="adm-empty">기록이 없어요</p>
    </div>
  </div>
</template>

<style scoped>
.adm-scrim { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 72; }
.adm { position: fixed; left: 50%; top: 50%; transform: translate(-50%,-50%); z-index: 73; width: min(620px, 94vw); max-height: 90vh; overflow-y: auto;
  background: var(--card); border-radius: 20px; padding: 18px 20px 22px; box-shadow: 0 20px 60px rgba(0,0,0,.35); }
.adm-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.adm-top b { font-size: 18px; }
.adm-x { border: 0; background: none; font: inherit; font-size: 20px; color: var(--pencil); cursor: pointer; }
.adm-tabs { display: flex; gap: 18px; border-bottom: 1px solid var(--line); margin-bottom: 16px; }
.adm-tabs button { border: 0; background: none; font: inherit; font-size: 14px; color: var(--pencil); cursor: pointer; padding: 6px 0 12px; position: relative; }
.adm-tabs button.sel { color: var(--ink); font-weight: 700; }
.adm-tabs button.sel::after { content: ''; position: absolute; left: 0; right: 0; bottom: -1px; height: 3px; border-radius: 2px; background: var(--marker); }
.adm-dash { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.adm-card { background: var(--paper-2); border-radius: 14px; padding: 16px; text-align: center; }
.adm-card b { display: block; font-size: 28px; font-variant-numeric: tabular-nums; }
.adm-card span { font-size: 12px; color: var(--pencil); }
.adm-search { display: flex; gap: 8px; margin-bottom: 10px; }
.adm-search input { flex: 1; min-height: 40px; border: 1px solid var(--line); background: var(--paper); border-radius: 10px; padding: 0 12px; font: inherit; font-size: 14px; color: var(--ink); }
.adm-search select, .adm-search button { min-height: 40px; border: 1px solid var(--line); background: var(--card); border-radius: 10px; padding: 0 12px; font: inherit; font-size: 13px; color: var(--ink); cursor: pointer; }
.adm-total { font-size: 12px; color: var(--pencil); margin-bottom: 8px; }
.adm-urow { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--line); flex-wrap: wrap; }
.adm-uinfo b { font-variant-numeric: tabular-nums; }
.adm-uinfo small { display: block; color: var(--pencil); font-size: 12px; margin-top: 3px; }
.adm-badge { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: var(--paper-2); margin-left: 6px; }
.adm-badge[data-s="suspended"] { color: #A8262B; } .adm-badge[data-s="pending_deletion"] { color: #B07A00; }
.adm-uact { display: flex; gap: 6px; flex-wrap: wrap; }
.adm-uact button { min-height: 34px; border: 1px solid var(--line); background: var(--card); border-radius: 8px; padding: 0 10px; font: inherit; font-size: 12px; color: var(--ink); cursor: pointer; }
.adm-uact button.danger { color: #A8262B; border-color: #A8262B; }
.adm-arow { padding: 10px 0; border-bottom: 1px solid var(--line); }
.adm-arow small { color: var(--pencil); font-size: 12px; }
.adm-empty { color: var(--pencil); font-size: 13px; text-align: center; padding: 20px; }
</style>
